import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui-components/ui/card';
import { Badge } from '../../../ui-components/ui/badge';
import { Button } from '../../../ui-components/ui/button';
import { MapPin, AlertCircle } from 'lucide-react';
import { cn } from '../../../lib/utils/utils';

interface PLZRegion {
  plz: string;
  name: string;
  assignedTo: string;
  leadsCount: number;
  conversionRate: number;
  revenue: number;
  status: 'hot' | 'warm' | 'cold' | 'unassigned';
}

const PLZDistribution: React.FC = () => {
  const [viewMode, setViewMode] = useState<'list' | 'heatmap'>('list');

  const plzRegions: PLZRegion[] = [
    {
      plz: '80',
      name: 'München',
      assignedTo: 'Max Mustermann',
      leadsCount: 45,
      conversionRate: 18.5,
      revenue: 125000,
      status: 'hot',
    },
    {
      plz: '81',
      name: 'München Süd',
      assignedTo: 'Anna Schmidt',
      leadsCount: 38,
      conversionRate: 15.2,
      revenue: 98000,
      status: 'warm',
    },
    {
      plz: '82',
      name: 'Starnberg',
      assignedTo: 'Thomas Weber',
      leadsCount: 22,
      conversionRate: 12.1,
      revenue: 67000,
      status: 'warm',
    },
    {
      plz: '83',
      name: 'Rosenheim',
      assignedTo: 'Julia Fischer',
      leadsCount: 15,
      conversionRate: 8.3,
      revenue: 45000,
      status: 'cold',
    },
    {
      plz: '84',
      name: 'Landshut',
      assignedTo: 'Unassigned',
      leadsCount: 8,
      conversionRate: 0,
      revenue: 0,
      status: 'unassigned',
    },
    {
      plz: '85',
      name: 'Freising',
      assignedTo: 'Michael Bauer',
      leadsCount: 28,
      conversionRate: 14.2,
      revenue: 78000,
      status: 'warm',
    },
  ];

  const getStatusColor = (status: PLZRegion['status']) => {
    switch (status) {
      case 'hot':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'warm':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'cold':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'unassigned':
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusBadge = (status: PLZRegion['status']) => {
    switch (status) {
      case 'hot':
        return <Badge className="bg-red-500 text-white">Hot</Badge>;
      case 'warm':
        return <Badge className="bg-orange-500 text-white">Warm</Badge>;
      case 'cold':
        return <Badge className="bg-blue-500 text-white">Cold</Badge>;
      case 'unassigned':
        return <Badge variant="secondary">Unassigned</Badge>;
    }
  };

  const HeatmapView = () => (
    <div className="grid grid-cols-6 gap-2 p-4">
      {plzRegions.map((region) => (
        <div
          key={region.plz}
          className={cn(
            "relative p-4 rounded-lg border-2 transition-all hover:scale-105 cursor-pointer",
            getStatusColor(region.status)
          )}
        >
          <div className="text-center">
            <p className="font-bold text-lg">{region.plz}</p>
            <p className="text-xs">{region.name}</p>
            <p className="text-xs font-semibold mt-1">{region.leadsCount} Leads</p>
          </div>
          {region.status === 'unassigned' && (
            <AlertCircle className="absolute top-1 right-1 h-4 w-4" />
          )}
        </div>
      ))}
    </div>
  );

  const ListView = () => (
    <div className="space-y-3">
      {plzRegions.map((region) => (
        <div
          key={region.plz}
          className={cn(
            "p-4 rounded-lg border",
            region.status === 'unassigned' && "bg-yellow-50 border-yellow-200"
          )}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="font-bold">{region.plz} - {region.name}</p>
                  <p className="text-sm text-gray-500">
                    {region.assignedTo === 'Unassigned' ? (
                      <span className="text-red-500 font-medium">Nicht zugewiesen</span>
                    ) : (
                      region.assignedTo
                    )}
                  </p>
                </div>
              </div>
              {getStatusBadge(region.status)}
            </div>

            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-sm text-gray-500">Leads</p>
                <p className="font-bold text-lg">{region.leadsCount}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500">CR</p>
                <p className="font-bold text-lg">{region.conversionRate}%</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500">Umsatz</p>
                <p className="font-bold text-lg">€{(region.revenue / 1000).toFixed(0)}k</p>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>PLZ-Verteilung</CardTitle>
            <CardDescription>Gebietsaufteilung und Performance</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              Liste
            </Button>
            <Button
              variant={viewMode === 'heatmap' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('heatmap')}
            >
              Heatmap
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {viewMode === 'list' ? <ListView /> : <HeatmapView />}
        
        {/* Summary */}
        <div className="mt-6 pt-4 border-t grid grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-sm text-gray-500">Gebiete</p>
            <p className="font-bold">{plzRegions.length}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Nicht zugewiesen</p>
            <p className="font-bold text-red-500">
              {plzRegions.filter(r => r.status === 'unassigned').length}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Hot Regions</p>
            <p className="font-bold text-green-500">
              {plzRegions.filter(r => r.status === 'hot').length}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Ø CR</p>
            <p className="font-bold">12.7%</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PLZDistribution;
